import { Redirect, Route, Switch } from 'react-router-dom';
import React, { memo } from 'react';
import { Container } from 'react-bootstrap';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Suspense from '../../shared/components/suspense';
import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import { IProps } from './types';
import retryLazy from '../retry-lazy';
import { RootState } from '../../store/root-reducer';
import { getIsDemoAccount } from '../../shared/utils/user-details';
import TestCreateUpdateFallback from './components/fallbacks/test-create-update-fallback';
import TestReportFallback from './components/fallbacks/test-report-fallback';
import DashboardFallback from './components/fallbacks/dashboard-fallback';

const Dashboard = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "dashboard" */ './components/dashboard/dashboard-container'
      ),
  ),
);
const TestCreateUpdate = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "test-create-update" */ './components/test-create-update'
      ),
  ),
);
const TestCreateUpdateExternal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "test-create-update-external" */ './components/test-create-update-external'
      ),
  ),
);
const TestReport = React.lazy(() =>
  retryLazy(
    () =>
      import(/* webpackChunkName: "test-report" */ './components/test-report'),
  ),
);

const InboxRadar: React.FC<IProps> = () => {
  const isVerified = useSelector((state: RootState) => state?.home?.verified);

  return (
    <Container
      fluid
      className={classNames('inbox-radar-container p-0', {
        'with-alert-message': !isVerified,
        'for-demo-account': getIsDemoAccount(),
      })}
    >
      <ErrorBoundaryWrapper>
        <Switch>
          <Route exact path="/inbox-radar/create-update-test/:testTemplateId">
            <Suspense fallback={<TestCreateUpdateFallback />}>
              <TestCreateUpdate />
            </Suspense>
          </Route>
          <Route
            exact
            path="/inbox-radar/create-update-test-external/:testTemplateId"
          >
            <Suspense>
              <TestCreateUpdateExternal />
            </Suspense>
          </Route>
          <Route exact path="/inbox-radar/report/:testId">
            <Suspense fallback={<TestReportFallback />}>
              <TestReport />
            </Suspense>
          </Route>
          <Route exact path="/inbox-radar">
            <Suspense fallback={<DashboardFallback />}>
              <Dashboard />
            </Suspense>
          </Route>
          <Redirect to="/inbox-radar" />
        </Switch>
      </ErrorBoundaryWrapper>
    </Container>
  );
};

function isPropsAreEqual(prevProps: IProps, nextProps: IProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(InboxRadar, isPropsAreEqual);
